import React, { Suspense, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  alpha,
  useTheme,
  InputBase,
  Chip,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  DriveFolderUpload as MigrationIcon,
  CompareArrows as CrossRefIcon,
  LocalShipping as SupplierIcon,
  AccountTree as BOMIcon,
  Settings as SettingsIcon,
  Help as HelpIcon,
  Slideshow as PresentationIcon,
  ChevronLeft as ChevronLeftIcon,
  ArrowBack as ArrowBackIcon,
  Engineering as EngineeringIcon,
  ShoppingCart as BuyerIcon,
  Receipt as RfqIcon,
  Description as ContractIcon,
  Handshake as PartnerIcon,
  Mail as TemplatesIcon,
  ExpandLess,
  ExpandMore,
  Hub as OrchestratorIcon,
  BarChart as BarChartIcon,
  Assessment as AssessmentIcon,
  PieChart as PieChartIcon,
  AutoAwesome as IntelligenceIcon,
  Tune as DomainConfigIcon,
  SyncAlt as MasterSyncIcon,
  QueryStats as AnalyticsDataIcon,
  Storage as DataOperationsIcon,
  Schema as WorkflowDesignerIcon,
  AccountTree as WorkflowsIcon,
  ListAlt as WorkflowDefinitionsIcon,
  History as WorkflowRunsIcon,
  Speed as WorkflowOperationsIcon,
  TrackChanges as OperationsInsightIcon,
  LocalOfferOutlined as ProcurementInsightIcon,
  OutlinedFlag as EngineeringInsightIcon,
  ShowChart as ExploreInsightIcon,
  AutoAwesomeOutlined as AskInsightIcon,
  Inventory2Outlined as ArchiveIcon,
  Insights as InsightsGroupIcon,
  StorageOutlined as CatalogInsightIcon,
  AdminPanelSettingsOutlined as AdministrationIcon,
  CloudOutlined as CloudManagerIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
// Logo served from /public so the navbar mark, browser favicon, and home-screen
// icon all read as the same brand artwork (orbital loop). favicon-192 is the
// retina-sharp size for the 40px slot.
const UVisionLogo = '/favicon-192.png';
import { CompanySwitcher, UserAvatarDropdown, useUserProfile, useAuth } from '@ciea.ai/smart-auth-lib';
import { BreadcrumbComponent, useAppSelector } from '@ciea.ai/smart-infra-navigation';
import ProcessMonitorWidget from '../ProcessMonitorWidget';
import NotificationBell from '../notifications/NotificationBell';
import CatalogAiAssistant from '../ai-assistant/CatalogAiAssistant';
import { CATALOG_PERMISSIONS } from '../../constants/permissions';
import { useQuery } from '@tanstack/react-query';
import { warehouseService } from '../../services/warehouseService';
import api from '../../services/api';

/** GET /access/me: the server's yes/no per declaration, from the signed-in user's levels and licence. */
interface AccessMe {
  signedIn: boolean;
  legacy: boolean;
  can: Record<string, boolean>;
}

// ============================================
// DARK THEME COLORS (Smart Procurement Hub style)
// ============================================

const darkShell = {
  // Sidebar & Header
  background: '#1a1f2e',
  backgroundHover: '#252b3b',
  backgroundActive: 'rgba(14, 165, 233, 0.15)',
  
  // Text
  textPrimary: '#ffffff',
  textSecondary: '#94a3b8',
  textMuted: '#64748b',
  
  // Accent
  accent: '#0ea5e9',
  accentHover: '#38bdf8',
  
  // Borders
  divider: '#2d3548',
  
  // Search
  searchBg: 'rgba(255, 255, 255, 0.08)',
  searchBgHover: 'rgba(255, 255, 255, 0.12)',
};

const drawerWidth = 260;

// Persisted UI state — sidebar collapse + group expansion. localStorage keys
// are namespaced under "catalog.sidebar." so other apps using their own
// AppLayout don't collide.
const LS_KEYS = {
  drawerOpen: 'catalog.sidebar.drawerOpen',
  orchestratorOpen: 'catalog.sidebar.orchestratorOpen',
  partnerNetworkOpen: 'catalog.sidebar.partnerNetworkOpen',
  analyticsOpen: 'catalog.sidebar.analyticsOpen',
  insightsOpen: 'catalog.sidebar.insightsOpen',
  workflowsOpen: 'catalog.sidebar.workflowsOpen',
  dataOperationsOpen: 'catalog.sidebar.dataOperationsOpen',
  administrationOpen: 'catalog.sidebar.administrationOpen',
  cloudManagerOpen: 'catalog.sidebar.cloudManagerOpen',
} as const;

const readBoolFromLS = (key: string, fallback: boolean): boolean => {
  try {
    const v = window.localStorage.getItem(key);
    if (v === null) return fallback;
    return v === 'true';
  } catch {
    return fallback;
  }
};

// Tooltip shown next to a collapsed sidebar icon. Dark theme matched to the
// rail's own palette (#1a1f2e shell). Hidden when the drawer is open since
// the label is already visible inline. placement="right" lifts it clear of
// the rail.
const SidebarTooltip: React.FC<{
  title: string;
  drawerOpen: boolean;
  children: React.ReactElement;
}> = ({ title, drawerOpen, children }) => (
  <Tooltip
    // `key` keyed off drawer state forces a fresh Tooltip mount when the
    // drawer transitions. Without this, Popper retains a stale bounding-box
    // measurement from before the drawer animation and the tooltip pops up
    // far from the anchor.
    key={drawerOpen ? 'open' : 'collapsed'}
    title={title}
    placement="right"
    arrow
    // When the drawer opens we want any currently-visible tooltip to dismiss
    // immediately, not linger until mouseout. `disableHoverListener` alone
    // prevents NEW tooltips but doesn't kill the existing one — passing
    // `open={false}` while drawerOpen forces it closed. When drawer is
    // collapsed we pass undefined so MUI manages hover normally.
    open={drawerOpen ? false : undefined}
    disableHoverListener={drawerOpen}
    disableFocusListener={drawerOpen}
    disableTouchListener={drawerOpen}
    enterDelay={200}
    enterNextDelay={100}
    // Wrap the anchor in a span so Popper measures a stable inline element
    // rather than a styled MUI button-component whose own transforms /
    // padding can throw off the bounding-box calculation.
    slotProps={{
      tooltip: {
        sx: {
          bgcolor: '#1a1f2e',
          color: '#ffffff',
          fontSize: '0.8125rem',
          fontWeight: 500,
          px: 1.25,
          py: 0.75,
          borderRadius: 1.5,
          boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
          letterSpacing: '0.01em',
        },
      },
      arrow: { sx: { color: '#1a1f2e' } },
      popper: {
        // Stick close to the anchor's right edge; no boundary-aware flipping
        // (we don't want it jumping to a different placement near a viewport
        // edge), and a single explicit offset so spacing is predictable.
        modifiers: [
          { name: 'offset', options: { offset: [0, 8] } },
          { name: 'flip', enabled: false },
          { name: 'preventOverflow', options: { boundary: 'viewport' } },
        ],
      },
    }}
  >
    <span style={{ display: 'flex', flexGrow: 1, minWidth: 0 }}>
      {children}
    </span>
  </Tooltip>
);

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path?: string;
  badge?: number;
  children?: NavItem[];
  /** Optional permission gate (anyOf). If omitted, item is visible to anyone with catalog:read. */
  permissions?: string[];
  /** Shown to platform admins only (our own cross-tenant data, never a customer's). */
  platformAdminOnly?: boolean;
  /**
   * What the item opens, as an @smart/access declaration (module.feature:action). With a token that
   * carries levels, the server decides visibility from this (GET /access/me); older tokens use `permissions`.
   */
  access?: string;
}

// Main navigation items - flat items
const mainNavItems: NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: <DashboardIcon />, path: '/', permissions: [CATALOG_PERMISSIONS.READ], access: 'catalog:view' },
  { id: 'parts', label: 'Parts Catalog', icon: <InventoryIcon />, path: '/parts', permissions: [CATALOG_PERMISSIONS.READ], access: 'catalog.parts:view' },
  { id: 'cross-references', label: 'Cross-References', icon: <CrossRefIcon />, path: '/cross-references', permissions: [CATALOG_PERMISSIONS.READ], access: 'catalog.cross-references:view' },
  { id: 'bom', label: 'BOM Intelligence', icon: <IntelligenceIcon />, path: '/bom', permissions: [CATALOG_PERMISSIONS.READ], access: 'catalog.boms:view' },
];

// Smart Orchestrator group — each queue is role-scoped
const orchestratorItems: NavItem = {
  id: 'orchestrator',
  label: 'Smart Orchestrator',
  icon: <OrchestratorIcon />,
  children: [
    { id: 'engineering', label: 'Engineering Review', icon: <EngineeringIcon />, path: '/engineering', permissions: [CATALOG_PERMISSIONS.REVIEW_ENGINEER], access: 'sourcing.engineering-review:view' },
    { id: 'buyer', label: 'Buyer Review', icon: <BuyerIcon />, path: '/buyer', permissions: [CATALOG_PERMISSIONS.REVIEW_BUYER], access: 'sourcing.buyer-review:view' },
    { id: 'rfq-center', label: 'RFQ Center', icon: <RfqIcon />, path: '/rfq', permissions: [CATALOG_PERMISSIONS.REVIEW_BUYER], access: 'sourcing.rfq:view' },
  ],
};

// Workflows group: the workflow engine (federated, mounted at /workflows/*):
// approval workflows and the part pipelines, both kept and edited there.
const workflowsItems: NavItem = {
  id: 'workflows',
  label: 'Workflows',
  icon: <WorkflowsIcon />,
  children: [
    // Approvals are answered in the orchestrator (Engineering / Buyer Review), the one
    // place people act; the engine's own inbox stays reachable at /workflows/inbox for admins.
    { id: 'wf-definitions', label: 'Process Designer', icon: <WorkflowDefinitionsIcon />, path: '/workflows/workflows', permissions: [CATALOG_PERMISSIONS.ADMIN], access: 'platform.process-design:view' },
    { id: 'wf-runs', label: 'Workflow Runs', icon: <WorkflowRunsIcon />, path: '/workflows/runs', permissions: [CATALOG_PERMISSIONS.READ], access: 'catalog:view' },
    { id: 'wf-record-types', label: 'Record Types', icon: <WorkflowDefinitionsIcon />, path: '/workflows/record-types', permissions: [CATALOG_PERMISSIONS.ADMIN], access: 'platform.process-design:view' },
    { id: 'part-pipelines', label: 'Part Pipelines', icon: <WorkflowDesignerIcon />, path: '/workflows/workflows?view=pipelines', permissions: [CATALOG_PERMISSIONS.ADMIN], access: 'platform.process-design:view' },
    // The engine's health: queues, workers, stuck work, cost.
    { id: 'wf-operations', label: 'Operations', icon: <WorkflowOperationsIcon />, path: '/workflows/operations', permissions: [CATALOG_PERMISSIONS.ADMIN], access: 'admin:manage' },
  ],
};

// External modules - flat (CMS is a federated app; reuse read for visibility)
const externalModules: NavItem[] = [
  { id: 'cms', label: 'Contract Management', icon: <ContractIcon />, path: '/cms', permissions: [CATALOG_PERMISSIONS.READ], access: 'sourcing:view' },
];

// Partner Network group
const partnerNetworkItems: NavItem = {
  id: 'partner-network',
  label: 'Partner Network',
  icon: <PartnerIcon />,
  children: [
    { id: 'partners', label: 'Partners', icon: <PartnerIcon />, path: '/partners', permissions: [CATALOG_PERMISSIONS.READ], access: 'catalog.supplier-network:view' },
    { id: 'comm-templates', label: 'Comm. Templates', icon: <TemplatesIcon />, path: '/partners/templates', permissions: [CATALOG_PERMISSIONS.ADMIN], access: 'sourcing:manage' },
  ],
};

// Insights: one home per responsibility (Operations, Procurement, Engineering), the shared
// tools (Explore, Ask) and AI cost. All read the analytics warehouse (BigQuery).
const insightsItems: NavItem = {
  id: 'insights-group',
  label: 'Insights',
  icon: <InsightsGroupIcon />,
  children: [
    { id: 'insights-operations', label: 'Operations', icon: <OperationsInsightIcon />, path: '/insights/operations', permissions: [CATALOG_PERMISSIONS.READ], access: 'catalog.insights:view' },
    { id: 'insights-procurement', label: 'Procurement', icon: <ProcurementInsightIcon />, path: '/insights/procurement', permissions: [CATALOG_PERMISSIONS.READ], access: 'sourcing.insights:view' },
    { id: 'insights-engineering', label: 'Engineering', icon: <EngineeringInsightIcon />, path: '/insights/engineering', permissions: [CATALOG_PERMISSIONS.READ], access: 'npi.insights:view' },
    { id: 'insights-catalog', label: 'Catalog', icon: <CatalogInsightIcon />, path: '/insights/catalog', permissions: [CATALOG_PERMISSIONS.READ], access: 'catalog.insights:view' },
    { id: 'insights-explore', label: 'Explore', icon: <ExploreInsightIcon />, path: '/insights/explore', permissions: [CATALOG_PERMISSIONS.READ], access: 'catalog.insights:view' },
    { id: 'insights-ask', label: 'Ask', icon: <AskInsightIcon />, path: '/insights/ask', permissions: [CATALOG_PERMISSIONS.READ], access: 'catalog.insights:view' },
  ],
};

// Archive: the earlier analytics pages (several run on mock data), kept reachable but
// out of the way now that Insights replaces them.
// Hidden from the sidebar for now. The pages and routes stay; flip to true to bring the group back.
const SHOW_ARCHIVE_MENU = false;

const analyticsItems: NavItem = {
  id: 'analytics-group',
  label: 'Archive',
  icon: <ArchiveIcon />,
  children: [
    { id: 'catalog-analytics', label: 'Catalog Analytics', icon: <BarChartIcon />, path: '/analytics/catalog', permissions: [CATALOG_PERMISSIONS.READ], access: 'platform.operations:view' },
    { id: 'analytics', label: 'Analytics Hub', icon: <BarChartIcon />, path: '/analytics', permissions: [CATALOG_PERMISSIONS.READ], access: 'platform.operations:view' },
    { id: 'mro-analytics', label: 'MRO Analytics', icon: <AssessmentIcon />, path: '/analytics/mro', permissions: [CATALOG_PERMISSIONS.ADMIN], access: 'platform.operations:view' },
    { id: 'extended-analytics', label: 'Executive Dashboard', icon: <PieChartIcon />, path: '/analytics/executive', permissions: [CATALOG_PERMISSIONS.READ], access: 'platform.operations:view' },
  ],
};

// The pages of the Data Operations group, at the bottom of the rail.
const dataOperationsItems = [
  { id: 'catalog-migration', label: 'Catalog Migration', path: '/catalog-migration', icon: <MigrationIcon fontSize="small" />, access: 'catalog.import-export:manage' },
  { id: 'master-catalog-sync', label: 'Master Catalog Sync', path: '/master-catalog/sync', icon: <MasterSyncIcon fontSize="small" />, access: 'platform.master-catalog:view' },
  { id: 'analytics-data', label: 'Analytics Data', path: '/analytics-data', icon: <AnalyticsDataIcon fontSize="small" />, access: 'platform.warehouse:view' },
];

// Administration (the company's own people, roles, company and plan) and Cloud Manager (uVizion
// staff: every customer). The screens come from the organization-management library.
const administrationItems = [
  { id: 'admin-overview', label: 'Overview', path: '/admin', exact: true, access: 'admin.users:view' },
  { id: 'admin-people', label: 'People', path: '/admin/people', access: 'admin.users:view' },
  { id: 'admin-roles', label: 'Roles', path: '/admin/roles', access: 'admin.users:view' },
  { id: 'admin-company', label: 'Company', path: '/admin/company', access: 'admin.company:view' },
  { id: 'admin-sign-in', label: 'Sign-in and security', path: '/admin/sign-in', access: 'admin.sign-in:view' },
  { id: 'admin-audit', label: 'Audit log', path: '/admin/audit', access: 'admin.users:view' },
];
const cloudManagerItems = [
  { id: 'cloud-companies', label: 'Companies', path: '/cloud/companies', access: 'platform.tenants:view' },
  { id: 'cloud-billing', label: 'Billing', path: '/cloud/billing', access: 'platform.billing:view' },
  { id: 'cloud-ai-cost', label: 'AI cost', path: '/cloud/ai-cost', access: 'platform.billing:view' },
  { id: 'cloud-agents', label: 'AI agents', path: '/cloud/agents', access: 'platform.agents:view' },
  { id: 'cloud-audit', label: 'Audit log', path: '/cloud/audit', access: 'platform.tenants:view' },
  { id: 'cloud-website', label: 'Website', path: '/insights/website', access: 'platform.marketing:view' },
];

// Each Cloud Manager section's all-companies page, for the company switcher ('' = Companies).
const CLOUD_FLEET: Record<string, string> = {
  '': '/cloud/companies',
  billing: '/cloud/billing',
  'ai-cost': '/cloud/ai-cost',
  agents: '/cloud/agents',
  audit: '/cloud/audit',
  website: '/insights/website',
};
const isCloudManagerPath = (path: string) => path.startsWith('/cloud') || path.startsWith('/insights/ai-cost') || path.startsWith('/insights/website');

interface RailChild {
  id: string;
  label: string;
  path: string;
  exact?: boolean;
}

// A collapsible group at the bottom of the rail: a parent row and its children, in the shell's look.
const RailGroup: React.FC<{
  label: string;
  icon: React.ReactNode;
  open: boolean;
  drawerOpen: boolean;
  items: RailChild[];
  isActive: (item: RailChild) => boolean;
  onToggle: () => void;
  onNavigate: (path: string) => void;
}> = ({ label, icon, open, drawerOpen, items, isActive, onToggle, onNavigate }) => {
  const anyActive = items.some(isActive);
  return (
    <>
      <ListItem disablePadding sx={{ mb: 0.5 }}>
        <SidebarTooltip title={label} drawerOpen={drawerOpen}>
          <ListItemButton
            onClick={onToggle}
            aria-label={label}
            aria-expanded={drawerOpen ? open : false}
            sx={{ borderRadius: 2, minHeight: 44, justifyContent: drawerOpen ? 'initial' : 'center', px: 2, '&:hover': { bgcolor: darkShell.backgroundHover } }}
          >
            <ListItemIcon sx={{ minWidth: 0, mr: drawerOpen ? 2 : 'auto', justifyContent: 'center', color: anyActive ? darkShell.textPrimary : darkShell.textSecondary }}>
              {icon}
            </ListItemIcon>
            {drawerOpen && (
              <>
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: anyActive ? 500 : 400, color: anyActive ? darkShell.textPrimary : darkShell.textSecondary }}
                />
                {open ? <ExpandLess sx={{ color: darkShell.textMuted }} /> : <ExpandMore sx={{ color: darkShell.textMuted }} />}
              </>
            )}
          </ListItemButton>
        </SidebarTooltip>
      </ListItem>
      <Collapse in={open && drawerOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {items.map((child) => {
            const active = isActive(child);
            return (
              <ListItem key={child.id} disablePadding sx={{ mb: 0.25 }}>
                <ListItemButton
                  onClick={() => onNavigate(child.path)}
                  sx={{
                    borderRadius: 2,
                    minHeight: 40,
                    pl: 7,
                    bgcolor: active ? darkShell.backgroundActive : 'transparent',
                    '&:hover': { bgcolor: active ? darkShell.backgroundActive : darkShell.backgroundHover },
                  }}
                >
                  <ListItemText
                    primary={child.label}
                    primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: active ? 600 : 400, color: active ? darkShell.textPrimary : darkShell.textSecondary }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Collapse>
    </>
  );
};

const AppLayout: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  // Drawer state — defaults to OPEN for first-time users so they can discover
  // the IA. Subsequent loads restore whatever they had. Persisting collapse
  // state is table stakes for enterprise UX (Stripe, Linear, Notion all do it).
  const [drawerOpen, setDrawerOpen] = useState<boolean>(() =>
    readBoolFromLS(LS_KEYS.drawerOpen, true),
  );
  // Group expansion — persisted independently so the user's mental layout of
  // the rail survives a page reload.
  const [orchestratorOpen, setOrchestratorOpen] = useState<boolean>(() =>
    readBoolFromLS(LS_KEYS.orchestratorOpen, false),
  );
  const [partnerNetworkOpen, setPartnerNetworkOpen] = useState<boolean>(() =>
    readBoolFromLS(LS_KEYS.partnerNetworkOpen, false),
  );
  const [analyticsOpen, setAnalyticsOpen] = useState<boolean>(() =>
    readBoolFromLS(LS_KEYS.analyticsOpen, false),
  );
  const [insightsOpen, setInsightsOpen] = useState<boolean>(() =>
    readBoolFromLS(LS_KEYS.insightsOpen, true),
  );
  const [workflowsOpen, setWorkflowsOpen] = useState<boolean>(() =>
    readBoolFromLS(LS_KEYS.workflowsOpen, false),
  );
  const [administrationOpen, setAdministrationOpen] = useState<boolean>(() =>
    readBoolFromLS(LS_KEYS.administrationOpen, false),
  );
  const [cloudManagerOpen, setCloudManagerOpen] = useState<boolean>(() =>
    readBoolFromLS(LS_KEYS.cloudManagerOpen, false),
  );
  const [dataOperationsOpen, setDataOperationsOpen] = useState<boolean>(() =>
    readBoolFromLS(LS_KEYS.dataOperationsOpen, false),
  );

  // Immersive surfaces (e.g. the RFQ Workspace tab) ask the rail to auto-collapse
  // for room, then restore the user's prior state on exit. We remember the
  // pre-collapse value in a ref and SKIP persisting while forced, so the user's
  // saved sidebar preference isn't clobbered by the temporary collapse.
  const preForcedDrawerRef = useRef<boolean | null>(null);
  useEffect(() => {
    const onSidebar = (e: Event) => {
      const collapse = !!(e as CustomEvent).detail?.collapse;
      if (collapse) {
        if (preForcedDrawerRef.current === null) preForcedDrawerRef.current = drawerOpen;
        setDrawerOpen(false);
      } else if (preForcedDrawerRef.current !== null) {
        const restore = preForcedDrawerRef.current;
        preForcedDrawerRef.current = null;
        setDrawerOpen(restore);
      }
    };
    window.addEventListener('uvizion:sidebar', onSidebar);
    return () => window.removeEventListener('uvizion:sidebar', onSidebar);
  }, [drawerOpen]);

  // Persist on change. Each effect is a one-liner — keeping them separate makes
  // them simple to reason about and avoids batching surprises.
  useEffect(() => { if (preForcedDrawerRef.current !== null) return; try { window.localStorage.setItem(LS_KEYS.drawerOpen, String(drawerOpen)); } catch {} }, [drawerOpen]);
  useEffect(() => { try { window.localStorage.setItem(LS_KEYS.orchestratorOpen, String(orchestratorOpen)); } catch {} }, [orchestratorOpen]);
  useEffect(() => { try { window.localStorage.setItem(LS_KEYS.partnerNetworkOpen, String(partnerNetworkOpen)); } catch {} }, [partnerNetworkOpen]);
  useEffect(() => { try { window.localStorage.setItem(LS_KEYS.analyticsOpen, String(analyticsOpen)); } catch {} }, [analyticsOpen]);
  useEffect(() => { try { window.localStorage.setItem(LS_KEYS.insightsOpen, String(insightsOpen)); } catch {} }, [insightsOpen]);
  useEffect(() => { try { window.localStorage.setItem(LS_KEYS.workflowsOpen, String(workflowsOpen)); } catch {} }, [workflowsOpen]);
  useEffect(() => { try { window.localStorage.setItem(LS_KEYS.dataOperationsOpen, String(dataOperationsOpen)); } catch {} }, [dataOperationsOpen]);
  useEffect(() => { try { window.localStorage.setItem(LS_KEYS.administrationOpen, String(administrationOpen)); } catch {} }, [administrationOpen]);
  useEffect(() => { try { window.localStorage.setItem(LS_KEYS.cloudManagerOpen, String(cloudManagerOpen)); } catch {} }, [cloudManagerOpen]);

  // Click handler for group headers — when the drawer is collapsed, a click on
  // a group icon was previously a no-op (the Collapse below is gated by
  // `drawerOpen`). Now: clicking a collapsed group icon EXPANDS the drawer AND
  // expands that group, so the children become reachable in a single click.
  // When the drawer is already open it behaves like a normal toggle.
  const handleGroupClick = useCallback(
    (setOpen: React.Dispatch<React.SetStateAction<boolean>>, currentlyOpen: boolean) => {
      if (!drawerOpen) {
        setDrawerOpen(true);
        setOpen(true);
      } else {
        setOpen(!currentlyOpen);
      }
    },
    [drawerOpen],
  );

  // Use the integrated useUserProfile hook from auth-lib
  const { isLoading } = useUserProfile();
  const { hasAnyPermission } = useAuth();

  // Contextual back chip — driven by `location.state.backTo` set by the source
  // page when it navigated here. Renders ABOVE the canonical breadcrumb. The
  // breadcrumb still shows the IA hierarchy (what's predictable per URL); the
  // chip shows where THIS user came from in their actual flow. Two questions,
  // two answers, no conflict.
  const backState = (location.state as {
    backTo?: string;
    backLabel?: string;
    returnTab?: string;
  } | null) || null;
  const showBackChip = !!backState?.backTo && !!backState?.backLabel;
  const handleBackChipClick = () => {
    if (!backState?.backTo) return;
    navigate(backState.backTo, {
      state: backState.returnTab ? { restoreTab: backState.returnTab } : undefined,
    });
  };

  // Breadcrumb visibility — render only when items belong to the CURRENT route.
  // Pages dispatch breadcrumb items into Redux on mount, but those items linger
  // after navigation. To avoid stale chrome (e.g. a "BOM Intelligence" trail
  // showing up on the Dashboard), we treat the breadcrumb as valid only when
  // the last item's path matches the current location (exact or as a parent
  // prefix). No effects, no race conditions — pure derived state.
  const breadcrumbItems = useAppSelector((state) => state.breadcrumb.items);
  const lastItemPath = breadcrumbItems[breadcrumbItems.length - 1]?.path;
  const showBreadcrumb =
    !!lastItemPath &&
    (location.pathname === lastItemPath ||
      location.pathname.startsWith(lastItemPath + '/'));

  // Visibility helper: items without `permissions` are always shown (catalog:read is
  // already enforced at the route level by App.tsx); items WITH `permissions` are
  // hidden when the user doesn't hold any of them.
  // Platform-admin-only entries (our own cross-tenant data) stay hidden until the check answers.
  const platformMe = useQuery({ queryKey: ['warehouse-me'], queryFn: () => warehouseService.me(), staleTime: 10 * 60_000 });
  // Levels and licence: the server decides from the same rule the API applies. Until the token
  // carries levels (older tokens) or while the call fails, the permission checks below stay in charge.
  const accessSpecs = useMemo(
    () => Array.from(new Set(['admin:manage', ...[...mainNavItems, ...(orchestratorItems.children ?? []), ...(workflowsItems.children ?? []),
      ...externalModules, ...(partnerNetworkItems.children ?? []), ...(insightsItems.children ?? []), ...(analyticsItems.children ?? []),
      ...dataOperationsItems, ...administrationItems, ...cloudManagerItems].map((i) => i.access).filter((a): a is string => Boolean(a))])),
    [],
  );
  const accessMe = useQuery({
    queryKey: ['access-me', accessSpecs],
    queryFn: async () => (await api.get<AccessMe>('/access/me', { params: { specs: accessSpecs.join(',') } })).data,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const levels = accessMe.data && accessMe.data.signedIn && !accessMe.data.legacy ? accessMe.data.can : null;
  const allowed = (spec: string | undefined) => (levels && spec ? levels[spec] === true : null);

  const canSeeItem = (item: NavItem | { access?: string }): boolean => {
    const decided = allowed(item.access);
    if (decided !== null) return decided;
    const legacy = item as NavItem;
    return (!legacy.platformAdminOnly || !!platformMe.data?.platformAdmin) &&
      (!legacy.permissions || legacy.permissions.length === 0 || hasAnyPermission(legacy.permissions));
  };
  const showAdminBlock = allowed('admin:manage') ?? hasAnyPermission([CATALOG_PERMISSIONS.ADMIN]);
  // Data Operations is for uVizion cloud staff only, like Cloud Manager.
  const isCloudStaff = allowed('platform.tenants:view') ?? !!platformMe.data?.platformAdmin;
  const visibleDataOperations = isCloudStaff ? dataOperationsItems.filter((item) => allowed(item.access) ?? true) : [];
  // Older tokens carry no levels: then admins see Administration and the platform role sees Cloud Manager.
  const visibleAdministration = administrationItems.filter((item) => allowed(item.access) ?? showAdminBlock);
  const visibleCloudManager = cloudManagerItems.filter((item) => allowed(item.access) ?? !!platformMe.data?.platformAdmin);
  const showCompanySwitcher = visibleCloudManager.length > 0 && isCloudManagerPath(location.pathname);
  const isActiveChild = (item: RailChild) =>
    item.exact ? location.pathname === item.path || location.pathname === `${item.path}/` : isActivePath(item.path);

  // Filter group children up-front so the parent (and its expand toggle) disappears
  // entirely when no children are visible for this user.
  const visibleOrchestratorChildren = orchestratorItems.children?.filter(canSeeItem) ?? [];
  const visiblePartnerNetworkChildren = partnerNetworkItems.children?.filter(canSeeItem) ?? [];
  const visibleAnalyticsChildren = analyticsItems.children?.filter(canSeeItem) ?? [];
  const visibleInsightsChildren = insightsItems.children?.filter(canSeeItem) ?? [];
  const visibleWorkflowsChildren = workflowsItems.children?.filter(canSeeItem) ?? [];

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const isActivePath = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8fafc' }}>
      {/* DARK App Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: `calc(100% - ${drawerOpen ? drawerWidth : 72}px)`,
          ml: `${drawerOpen ? drawerWidth : 72}px`,
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          bgcolor: darkShell.background,
          borderBottom: `1px solid ${darkShell.divider}`,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={handleDrawerToggle}
              sx={{ 
                color: darkShell.textSecondary,
                '&:hover': { color: darkShell.textPrimary, bgcolor: darkShell.backgroundHover },
              }}
            >
              {drawerOpen ? <ChevronLeftIcon /> : <MenuIcon />}
            </IconButton>

            {/* Cloud Manager: pick a company once; every section follows it. */}
            {showCompanySwitcher && <CompanySwitcher cloudPath="/cloud" fleet={CLOUD_FLEET} />}

            {/* Search Bar - Dark Style */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                bgcolor: darkShell.searchBg,
                borderRadius: 2,
                px: 2,
                py: 0.5,
                width: showCompanySwitcher ? 300 : 400,
                '&:hover': {
                  bgcolor: darkShell.searchBgHover,
                },
                '&:focus-within': {
                  bgcolor: darkShell.searchBgHover,
                  boxShadow: `0 0 0 2px ${darkShell.accent}`,
                },
              }}
            >
              <SearchIcon sx={{ color: darkShell.textSecondary, mr: 1 }} />
              <InputBase
                placeholder="Search parts, suppliers, BOMs..."
                sx={{ 
                  flex: 1, 
                  fontSize: '0.875rem',
                  color: darkShell.textPrimary,
                  '& input::placeholder': {
                    color: darkShell.textSecondary,
                    opacity: 1,
                  },
                }}
              />
              <Chip
                label="⌘K"
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  bgcolor: alpha('#ffffff', 0.1),
                  color: darkShell.textSecondary,
                }}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Presentation">
              <IconButton
                onClick={() => window.open(`${import.meta.env.BASE_URL}deck/index.html`, '_blank', 'noopener,noreferrer')}
                sx={{
                  color: darkShell.textSecondary,
                  '&:hover': { color: darkShell.textPrimary, bgcolor: darkShell.backgroundHover },
                }}
              >
                <PresentationIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Help">
              <IconButton sx={{
                color: darkShell.textSecondary,
                '&:hover': { color: darkShell.textPrimary, bgcolor: darkShell.backgroundHover },
              }}>
                <HelpIcon />
              </IconButton>
            </Tooltip>

            <NotificationBell
              iconColor={darkShell.textSecondary}
              hoverSx={{ '&:hover': { color: darkShell.textPrimary, bgcolor: darkShell.backgroundHover } }}
            />

            <ProcessMonitorWidget
              buttonSx={{
                color: darkShell.textSecondary,
                '&:hover': { color: darkShell.textPrimary, bgcolor: darkShell.backgroundHover },
              }}
            />

            <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: darkShell.divider }} />

            {/* User Avatar Dropdown from auth-lib */}
            <UserAvatarDropdown
              size="medium"
              showNotificationBadge={false}
              notificationCount={0}
              onThemeToggle={() => {
                console.log('Theme toggle clicked');
              }}
              onHelpClick={() => {
                console.log('Help clicked');
              }}
              disabled={isLoading}
            />
          </Box>
        </Toolbar>
      </AppBar>

      {/* DARK Sidebar Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          width: drawerOpen ? drawerWidth : 72,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerOpen ? drawerWidth : 72,
            boxSizing: 'border-box',
            borderRight: 'none',
            bgcolor: darkShell.background,
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
            overflowX: 'hidden',
          },
        }}
      >
        {/* Logo — clickable, navigates to Home. Enterprise convention
            (Stripe, Linear, GitHub all do this). No tooltip — the brand mark
            is self-evident and a hover hint is redundant. aria-label keeps
            screen readers informed. */}
        <Box
          role="button"
          tabIndex={0}
          aria-label="Go to home"
          onClick={() => navigate('/')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate('/');
            }
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2.5,
            py: 2,
            minHeight: 64,
            cursor: 'pointer',
            outline: 'none',
            '&:hover': { bgcolor: darkShell.backgroundHover },
            '&:focus-visible': {
              boxShadow: `inset 0 0 0 2px ${darkShell.accent}`,
            },
            transition: 'background-color 120ms ease',
          }}
        >
          <Box
            component="img"
            src={UVisionLogo}
            srcSet={`${UVisionLogo} 1x, /favicon-512.png 2x`}
            alt="uVizion"
            draggable={false}
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              userSelect: 'none',
            }}
          />
          <AnimatePresence>
            {drawerOpen && (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.2 }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontFamily:
                      'Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
                    fontWeight: 800,
                    fontSize: '1.375rem',
                    lineHeight: 1,
                    letterSpacing: '-0.02em',
                    color: darkShell.textPrimary,
                    whiteSpace: 'nowrap',
                  }}
                >
                  uVizion
                </Typography>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>

        <Divider sx={{ borderColor: darkShell.divider }} />

        {/* Navigation */}
        <List sx={{ px: 1.5, py: 2 }}>
          {/* Main Nav Items */}
          {mainNavItems.filter(canSeeItem).map((item) => {
            const isActive = isActivePath(item.path!);
            return (
              <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
                <SidebarTooltip title={item.label} drawerOpen={drawerOpen}>
                  <ListItemButton
                    onClick={() => navigate(item.path!)}
                    aria-label={item.label}
                    sx={{
                      borderRadius: 2,
                      minHeight: 44,
                      justifyContent: drawerOpen ? 'initial' : 'center',
                      px: 2,
                      bgcolor: isActive ? darkShell.backgroundActive : 'transparent',
                      '&:hover': {
                        bgcolor: isActive ? darkShell.backgroundActive : darkShell.backgroundHover,
                      },
                      position: 'relative',
                    }}
                  >
                    {isActive && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 0,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: 3,
                          height: 24,
                          bgcolor: darkShell.accent,
                          borderRadius: '0 4px 4px 0',
                        }}
                      />
                    )}
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: drawerOpen ? 2 : 'auto',
                        justifyContent: 'center',
                        color: isActive ? darkShell.accent : darkShell.textSecondary,
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {drawerOpen && (
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: '0.875rem',
                          fontWeight: isActive ? 600 : 400,
                          color: isActive ? darkShell.textPrimary : darkShell.textSecondary,
                        }}
                      />
                    )}
                  </ListItemButton>
                </SidebarTooltip>
              </ListItem>
            );
          })}

          {/* Smart Orchestrator Group */}
          {visibleOrchestratorChildren.length > 0 && (<>
          <ListItem disablePadding sx={{ mb: 0.5, mt: 1.5 }}>
            <SidebarTooltip title={orchestratorItems.label} drawerOpen={drawerOpen}>
              <ListItemButton
                onClick={() => handleGroupClick(setOrchestratorOpen, orchestratorOpen)}
                aria-label={orchestratorItems.label}
                aria-expanded={drawerOpen ? orchestratorOpen : false}
                sx={{
                  borderRadius: 2,
                  minHeight: 44,
                  justifyContent: drawerOpen ? 'initial' : 'center',
                  px: 2,
                  '&:hover': { bgcolor: darkShell.backgroundHover },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: drawerOpen ? 2 : 'auto',
                    justifyContent: 'center',
                    color: darkShell.textSecondary,
                  }}
                >
                  {orchestratorItems.icon}
                </ListItemIcon>
                {drawerOpen && (
                  <>
                    <ListItemText
                      primary={orchestratorItems.label}
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: darkShell.textSecondary,
                      }}
                    />
                    {orchestratorOpen ? <ExpandLess sx={{ color: darkShell.textMuted }} /> : <ExpandMore sx={{ color: darkShell.textMuted }} />}
                  </>
                )}
              </ListItemButton>
            </SidebarTooltip>
          </ListItem>
          <Collapse in={orchestratorOpen && drawerOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {visibleOrchestratorChildren.map((child) => {
                const isActive = isActivePath(child.path!);
                return (
                  <ListItem key={child.id} disablePadding sx={{ mb: 0.25 }}>
                    <ListItemButton
                      onClick={() => navigate(child.path!)}
                      sx={{
                        borderRadius: 2,
                        minHeight: 40,
                        pl: 4,
                        bgcolor: isActive ? darkShell.backgroundActive : 'transparent',
                        '&:hover': {
                          bgcolor: isActive ? darkShell.backgroundActive : darkShell.backgroundHover,
                        },
                        position: 'relative',
                      }}
                    >
                      {isActive && (
                        <Box
                          sx={{
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 3,
                            height: 20,
                            bgcolor: darkShell.accent,
                            borderRadius: '0 4px 4px 0',
                          }}
                        />
                      )}
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: 2,
                          justifyContent: 'center',
                          color: isActive ? darkShell.accent : darkShell.textMuted,
                        }}
                      >
                        {child.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={child.label}
                        primaryTypographyProps={{
                          fontSize: '0.8rem',
                          fontWeight: isActive ? 600 : 400,
                          color: isActive ? darkShell.textPrimary : darkShell.textMuted,
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Collapse>
          </>)}

          {/* Workflows Group */}
          {visibleWorkflowsChildren.length > 0 && (<>
          <ListItem disablePadding sx={{ mb: 0.5, mt: 1.5 }}>
            <SidebarTooltip title={workflowsItems.label} drawerOpen={drawerOpen}>
              <ListItemButton
                onClick={() => handleGroupClick(setWorkflowsOpen, workflowsOpen)}
                aria-label={workflowsItems.label}
                aria-expanded={drawerOpen ? workflowsOpen : false}
                sx={{
                  borderRadius: 2,
                  minHeight: 44,
                  justifyContent: drawerOpen ? 'initial' : 'center',
                  px: 2,
                  '&:hover': { bgcolor: darkShell.backgroundHover },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: drawerOpen ? 2 : 'auto',
                    justifyContent: 'center',
                    color: darkShell.textSecondary,
                  }}
                >
                  {workflowsItems.icon}
                </ListItemIcon>
                {drawerOpen && (
                  <>
                    <ListItemText
                      primary={workflowsItems.label}
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: darkShell.textSecondary,
                      }}
                    />
                    {workflowsOpen ? <ExpandLess sx={{ color: darkShell.textMuted }} /> : <ExpandMore sx={{ color: darkShell.textMuted }} />}
                  </>
                )}
              </ListItemButton>
            </SidebarTooltip>
          </ListItem>
          <Collapse in={workflowsOpen && drawerOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {visibleWorkflowsChildren.map((child) => {
                const isActive = isActivePath(child.path!);
                return (
                  <ListItem key={child.id} disablePadding sx={{ mb: 0.25 }}>
                    <ListItemButton
                      onClick={() => navigate(child.path!)}
                      sx={{
                        borderRadius: 2,
                        minHeight: 40,
                        pl: 4,
                        bgcolor: isActive ? darkShell.backgroundActive : 'transparent',
                        '&:hover': {
                          bgcolor: isActive ? darkShell.backgroundActive : darkShell.backgroundHover,
                        },
                        position: 'relative',
                      }}
                    >
                      {isActive && (
                        <Box
                          sx={{
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 3,
                            height: 20,
                            bgcolor: darkShell.accent,
                            borderRadius: '0 4px 4px 0',
                          }}
                        />
                      )}
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: 2,
                          justifyContent: 'center',
                          color: isActive ? darkShell.accent : darkShell.textMuted,
                        }}
                      >
                        {child.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={child.label}
                        primaryTypographyProps={{
                          fontSize: '0.8rem',
                          fontWeight: isActive ? 600 : 400,
                          color: isActive ? darkShell.textPrimary : darkShell.textMuted,
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Collapse>
          </>)}

          {/* External Modules */}
          {externalModules.filter(canSeeItem).map((item) => {
            const isActive = isActivePath(item.path!);
            return (
              <ListItem key={item.id} disablePadding sx={{ mb: 0.5, mt: item.id === 'cms' ? 1.5 : 0 }}>
                <SidebarTooltip title={item.label} drawerOpen={drawerOpen}>
                  <ListItemButton
                    onClick={() => navigate(item.path!)}
                    aria-label={item.label}
                    sx={{
                      borderRadius: 2,
                      minHeight: 44,
                      justifyContent: drawerOpen ? 'initial' : 'center',
                      px: 2,
                      bgcolor: isActive ? darkShell.backgroundActive : 'transparent',
                      '&:hover': {
                        bgcolor: isActive ? darkShell.backgroundActive : darkShell.backgroundHover,
                      },
                      position: 'relative',
                    }}
                  >
                    {isActive && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 0,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: 3,
                          height: 24,
                          bgcolor: darkShell.accent,
                          borderRadius: '0 4px 4px 0',
                        }}
                      />
                    )}
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: drawerOpen ? 2 : 'auto',
                        justifyContent: 'center',
                        color: isActive ? darkShell.accent : darkShell.textSecondary,
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {drawerOpen && (
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: '0.875rem',
                          fontWeight: isActive ? 600 : 400,
                          color: isActive ? darkShell.textPrimary : darkShell.textSecondary,
                        }}
                      />
                    )}
                  </ListItemButton>
                </SidebarTooltip>
              </ListItem>
            );
          })}

          {/* Partner Network Group */}
          {visiblePartnerNetworkChildren.length > 0 && (<>
          <ListItem disablePadding sx={{ mb: 0.5, mt: 1.5 }}>
            <SidebarTooltip title={partnerNetworkItems.label} drawerOpen={drawerOpen}>
              <ListItemButton
                onClick={() => handleGroupClick(setPartnerNetworkOpen, partnerNetworkOpen)}
                aria-label={partnerNetworkItems.label}
                aria-expanded={drawerOpen ? partnerNetworkOpen : false}
                sx={{
                  borderRadius: 2,
                  minHeight: 44,
                  justifyContent: drawerOpen ? 'initial' : 'center',
                  px: 2,
                  '&:hover': { bgcolor: darkShell.backgroundHover },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: drawerOpen ? 2 : 'auto',
                    justifyContent: 'center',
                    color: darkShell.textSecondary,
                  }}
                >
                  {partnerNetworkItems.icon}
                </ListItemIcon>
                {drawerOpen && (
                  <>
                    <ListItemText
                      primary={partnerNetworkItems.label}
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: darkShell.textSecondary,
                      }}
                    />
                    {partnerNetworkOpen ? <ExpandLess sx={{ color: darkShell.textMuted }} /> : <ExpandMore sx={{ color: darkShell.textMuted }} />}
                  </>
                )}
              </ListItemButton>
            </SidebarTooltip>
          </ListItem>
          <Collapse in={partnerNetworkOpen && drawerOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {visiblePartnerNetworkChildren.map((child) => {
                const isActive = isActivePath(child.path!);
                return (
                  <ListItem key={child.id} disablePadding sx={{ mb: 0.25 }}>
                    <ListItemButton
                      onClick={() => navigate(child.path!)}
                      sx={{
                        borderRadius: 2,
                        minHeight: 40,
                        pl: 4,
                        bgcolor: isActive ? darkShell.backgroundActive : 'transparent',
                        '&:hover': {
                          bgcolor: isActive ? darkShell.backgroundActive : darkShell.backgroundHover,
                        },
                        position: 'relative',
                      }}
                    >
                      {isActive && (
                        <Box
                          sx={{
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 3,
                            height: 20,
                            bgcolor: darkShell.accent,
                            borderRadius: '0 4px 4px 0',
                          }}
                        />
                      )}
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: 2,
                          justifyContent: 'center',
                          color: isActive ? darkShell.accent : darkShell.textMuted,
                        }}
                      >
                        {child.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={child.label}
                        primaryTypographyProps={{
                          fontSize: '0.8rem',
                          fontWeight: isActive ? 600 : 400,
                          color: isActive ? darkShell.textPrimary : darkShell.textMuted,
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Collapse>
          </>)}

          {/* Insights Group */}
          {visibleInsightsChildren.length > 0 && (<>
          <ListItem disablePadding sx={{ mb: 0.5, mt: 1.5 }}>
            <SidebarTooltip title={insightsItems.label} drawerOpen={drawerOpen}>
              <ListItemButton
                onClick={() => handleGroupClick(setInsightsOpen, insightsOpen)}
                aria-label={insightsItems.label}
                aria-expanded={drawerOpen ? insightsOpen : false}
                sx={{
                  borderRadius: 2,
                  minHeight: 44,
                  justifyContent: drawerOpen ? 'initial' : 'center',
                  px: 2,
                  '&:hover': { bgcolor: darkShell.backgroundHover },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: drawerOpen ? 2 : 'auto',
                    justifyContent: 'center',
                    color: darkShell.textSecondary,
                  }}
                >
                  {insightsItems.icon}
                </ListItemIcon>
                {drawerOpen && (
                  <>
                    <ListItemText
                      primary={insightsItems.label}
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: darkShell.textSecondary,
                      }}
                    />
                    {insightsOpen ? <ExpandLess sx={{ color: darkShell.textMuted }} /> : <ExpandMore sx={{ color: darkShell.textMuted }} />}
                  </>
                )}
              </ListItemButton>
            </SidebarTooltip>
          </ListItem>
          <Collapse in={insightsOpen && drawerOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {visibleInsightsChildren.map((child) => {
                const isActive = isActivePath(child.path!);
                return (
                  <ListItem key={child.id} disablePadding sx={{ mb: 0.25 }}>
                    <ListItemButton
                      onClick={() => navigate(child.path!)}
                      sx={{
                        borderRadius: 2,
                        minHeight: 40,
                        pl: 4,
                        bgcolor: isActive ? darkShell.backgroundActive : 'transparent',
                        '&:hover': {
                          bgcolor: isActive ? darkShell.backgroundActive : darkShell.backgroundHover,
                        },
                        position: 'relative',
                      }}
                    >
                      {isActive && (
                        <Box
                          sx={{
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 3,
                            height: 20,
                            bgcolor: darkShell.accent,
                            borderRadius: '0 4px 4px 0',
                          }}
                        />
                      )}
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: 2,
                          justifyContent: 'center',
                          color: isActive ? darkShell.accent : darkShell.textMuted,
                        }}
                      >
                        {child.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={child.label}
                        primaryTypographyProps={{
                          fontSize: '0.8rem',
                          fontWeight: isActive ? 600 : 400,
                          color: isActive ? darkShell.textPrimary : darkShell.textMuted,
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Collapse>
          </>)}

          {/* Analytics Group */}
          {SHOW_ARCHIVE_MENU && visibleAnalyticsChildren.length > 0 && (<>
          <ListItem disablePadding sx={{ mb: 0.5, mt: 1.5 }}>
            <SidebarTooltip title={analyticsItems.label} drawerOpen={drawerOpen}>
              <ListItemButton
                onClick={() => handleGroupClick(setAnalyticsOpen, analyticsOpen)}
                aria-label={analyticsItems.label}
                aria-expanded={drawerOpen ? analyticsOpen : false}
                sx={{
                  borderRadius: 2,
                  minHeight: 44,
                  justifyContent: drawerOpen ? 'initial' : 'center',
                  px: 2,
                  '&:hover': { bgcolor: darkShell.backgroundHover },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: drawerOpen ? 2 : 'auto',
                    justifyContent: 'center',
                    color: darkShell.textSecondary,
                  }}
                >
                  {analyticsItems.icon}
                </ListItemIcon>
                {drawerOpen && (
                  <>
                    <ListItemText
                      primary={analyticsItems.label}
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: darkShell.textSecondary,
                      }}
                    />
                    {analyticsOpen ? <ExpandLess sx={{ color: darkShell.textMuted }} /> : <ExpandMore sx={{ color: darkShell.textMuted }} />}
                  </>
                )}
              </ListItemButton>
            </SidebarTooltip>
          </ListItem>
          <Collapse in={analyticsOpen && drawerOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {visibleAnalyticsChildren.map((child) => {
                const isActive = isActivePath(child.path!);
                return (
                  <ListItem key={child.id} disablePadding sx={{ mb: 0.25 }}>
                    <ListItemButton
                      onClick={() => navigate(child.path!)}
                      sx={{
                        borderRadius: 2,
                        minHeight: 40,
                        pl: 4,
                        bgcolor: isActive ? darkShell.backgroundActive : 'transparent',
                        '&:hover': {
                          bgcolor: isActive ? darkShell.backgroundActive : darkShell.backgroundHover,
                        },
                        position: 'relative',
                      }}
                    >
                      {isActive && (
                        <Box
                          sx={{
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 3,
                            height: 20,
                            bgcolor: darkShell.accent,
                            borderRadius: '0 4px 4px 0',
                          }}
                        />
                      )}
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: 2,
                          justifyContent: 'center',
                          color: isActive ? darkShell.accent : darkShell.textMuted,
                        }}
                      >
                        {child.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={child.label}
                        primaryTypographyProps={{
                          fontSize: '0.8rem',
                          fontWeight: isActive ? 600 : 400,
                          color: isActive ? darkShell.textPrimary : darkShell.textMuted,
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Collapse>
          </>)}
        </List>

        <Box sx={{ flexGrow: 1 }} />

        {/* Admin & Settings at bottom: Domain Config hidden for non-admins (part pipelines moved to the Workflows group) */}
        <Divider sx={{ borderColor: darkShell.divider }} />
        <List sx={{ px: 1.5, py: 2 }}>
          {visibleCloudManager.length > 0 && (
            <RailGroup
              label="Cloud Manager"
              icon={<CloudManagerIcon />}
              open={cloudManagerOpen}
              drawerOpen={drawerOpen}
              items={visibleCloudManager}
              isActive={isActiveChild}
              onToggle={() => handleGroupClick(setCloudManagerOpen, cloudManagerOpen)}
              onNavigate={navigate}
            />
          )}
          {visibleAdministration.length > 0 && (
            <RailGroup
              label="Administration"
              icon={<AdministrationIcon />}
              open={administrationOpen}
              drawerOpen={drawerOpen}
              items={visibleAdministration}
              isActive={isActiveChild}
              onToggle={() => handleGroupClick(setAdministrationOpen, administrationOpen)}
              onNavigate={navigate}
            />
          )}
          {showAdminBlock && (<>
          <ListItem disablePadding>
            <SidebarTooltip title="Domain Config" drawerOpen={drawerOpen}>
              <ListItemButton
                onClick={() => navigate('/domain-config')}
                selected={location.pathname === '/domain-config'}
                aria-label="Domain Config"
                sx={{
                  borderRadius: 2,
                  minHeight: 44,
                  justifyContent: drawerOpen ? 'initial' : 'center',
                  px: 2,
                  mb: 0.5,
                  '&:hover': { bgcolor: darkShell.backgroundHover },
                  '&.Mui-selected': { bgcolor: darkShell.backgroundActive },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: drawerOpen ? 2 : 'auto',
                    justifyContent: 'center',
                    color: location.pathname === '/domain-config' ? darkShell.textPrimary : darkShell.textSecondary,
                  }}
                >
                  <DomainConfigIcon />
                </ListItemIcon>
                {drawerOpen && (
                  <ListItemText
                    primary="Domain Config"
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: location.pathname === '/domain-config' ? 500 : 400,
                      color: location.pathname === '/domain-config' ? darkShell.textPrimary : darkShell.textSecondary,
                    }}
                  />
                )}
              </ListItemButton>
            </SidebarTooltip>
          </ListItem>
          {/* Data Operations: every sync and data move in one place (uVizion cloud staff only). */}
          {visibleDataOperations.length > 0 && (<>
          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <SidebarTooltip title="Data Operations" drawerOpen={drawerOpen}>
              <ListItemButton
                onClick={() => handleGroupClick(setDataOperationsOpen, dataOperationsOpen)}
                aria-label="Data Operations"
                aria-expanded={drawerOpen ? dataOperationsOpen : false}
                sx={{
                  borderRadius: 2,
                  minHeight: 44,
                  justifyContent: drawerOpen ? 'initial' : 'center',
                  px: 2,
                  '&:hover': { bgcolor: darkShell.backgroundHover },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: drawerOpen ? 2 : 'auto',
                    justifyContent: 'center',
                    color: visibleDataOperations.some((item) => isActivePath(item.path))
                      ? darkShell.textPrimary
                      : darkShell.textSecondary,
                  }}
                >
                  <DataOperationsIcon />
                </ListItemIcon>
                {drawerOpen && (
                  <>
                    <ListItemText
                      primary="Data Operations"
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: 400,
                        color: darkShell.textSecondary,
                      }}
                    />
                    {dataOperationsOpen ? <ExpandLess sx={{ color: darkShell.textMuted }} /> : <ExpandMore sx={{ color: darkShell.textMuted }} />}
                  </>
                )}
              </ListItemButton>
            </SidebarTooltip>
          </ListItem>
          <Collapse in={dataOperationsOpen && drawerOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {visibleDataOperations.map((child) => {
                const isActive = isActivePath(child.path);
                return (
                  <ListItem key={child.id} disablePadding sx={{ mb: 0.25 }}>
                    <ListItemButton
                      onClick={() => navigate(child.path)}
                      sx={{
                        borderRadius: 2,
                        minHeight: 40,
                        pl: 4,
                        bgcolor: isActive ? darkShell.backgroundActive : 'transparent',
                        '&:hover': {
                          bgcolor: isActive ? darkShell.backgroundActive : darkShell.backgroundHover,
                        },
                        position: 'relative',
                      }}
                    >
                      {isActive && (
                        <Box
                          sx={{
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 3,
                            height: 20,
                            bgcolor: darkShell.accent,
                            borderRadius: '0 4px 4px 0',
                          }}
                        />
                      )}
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: 2,
                          justifyContent: 'center',
                          color: isActive ? darkShell.accent : darkShell.textMuted,
                        }}
                      >
                        {child.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={child.label}
                        primaryTypographyProps={{
                          fontSize: '0.8rem',
                          fontWeight: isActive ? 600 : 400,
                          color: isActive ? darkShell.textPrimary : darkShell.textMuted,
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Collapse>
          </>)}
          </>)}
          <ListItem disablePadding>
            <SidebarTooltip title="Settings" drawerOpen={drawerOpen}>
              <ListItemButton
                onClick={() => navigate('/settings')}
                aria-label="Settings"
                sx={{
                  borderRadius: 2,
                  minHeight: 44,
                  justifyContent: drawerOpen ? 'initial' : 'center',
                  px: 2,
                  '&:hover': {
                    bgcolor: darkShell.backgroundHover,
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: drawerOpen ? 2 : 'auto',
                    justifyContent: 'center',
                    color: darkShell.textSecondary,
                  }}
                >
                  <SettingsIcon />
                </ListItemIcon>
                {drawerOpen && (
                  <ListItemText
                    primary="Settings"
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: 400,
                      color: darkShell.textSecondary,
                    }}
                  />
                )}
              </ListItemButton>
            </SidebarTooltip>
          </ListItem>
        </List>
      </Drawer>

      {/* Main Content - Light background */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          overflow: 'hidden',
          px: 3,
          pt: 10,
          pb: 3,
          minHeight: '100vh',
          bgcolor: '#f8fafc',
        }}
      >
        {showBackChip && (
          <Box sx={{ mb: 1, px: 0.5 }}>
            <Box
              component="button"
              onClick={handleBackChipClick}
              aria-label={`Back to ${backState!.backLabel}`}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.5,
                border: 'none',
                bgcolor: 'transparent',
                color: theme.palette.text.secondary,
                fontSize: '0.8125rem',
                fontWeight: 500,
                cursor: 'pointer',
                borderRadius: 1,
                fontFamily: 'inherit',
                '&:hover': {
                  color: theme.palette.text.primary,
                  bgcolor: theme.palette.action.hover,
                },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 16 }} />
              {backState!.backLabel}
            </Box>
          </Box>
        )}
        {showBreadcrumb && (
          <Box
            sx={{
              mb: 2,
              px: 0.5,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <BreadcrumbComponent
              homeRoute="/"
              homeLabel="Home"
              showHomeIcon
              lightMode={false}
              darkMode={false}
              density="compact"
            />
          </Box>
        )}
        {/* A page still loading its code waits here, inside the content area; the menu and header stay. */}
        <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress size={28} /></Box>}>
          <Outlet />
        </Suspense>
      </Box>

      {/* AI assistant: parts, specs, pricing (federated from smart-ai-agent) */}
      <CatalogAiAssistant />
    </Box>
  );
};

export default AppLayout;